import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ModalSimple from '../../../components/ModalSimple';

/**
 * Confirmación de una operación en masa. No hay deshacer ni historial
 * consultable, así que este modal es la única barrera real: muestra siempre el
 * cambio concreto y el recuento exacto devuelto por el preview.
 *
 * Cuando el cambio hace que N assignments pasen a ser elegibles para el AI
 * Agent (pending + asignado al agente + fecha vencida), el agente empezará a
 * enviar WhatsApp/SMS/email REALES a esos deudores. En ese caso se exige una
 * confirmación adicional explícita.
 */
const BulkConfirmModal = ({ show, preview, changeDescriptions = [], onConfirm, onCancel, applying }) => {
  const [aiAcknowledged, setAiAcknowledged] = useState(false);

  useEffect(() => {
    if (show) setAiAcknowledged(false);
  }, [show]);

  if (!preview) return null;

  const aiCount = preview.ai_triggering_count || 0;
  const blocked = aiCount > 0 && !aiAcknowledged;

  return (
    <ModalSimple
      id='bulk-confirm-modal'
      title='Confirmar modificación en masa'
      show={show}
      onClose={onCancel}
      size='lg'
    >
      <div className='space-y-4'>
        <p className='text-sm text-slate-700'>
          Se van a modificar <span className='font-semibold'>{preview.affected}</span> assignment
          {preview.affected === 1 ? '' : 's'}.
        </p>

        <div className='p-3 bg-slate-50 border border-slate-200 rounded-lg'>
          <p className='text-xs uppercase font-semibold text-slate-500 mb-1'>
            {changeDescriptions.length === 1 ? 'Cambio a aplicar' : 'Cambios a aplicar'}
          </p>
          {changeDescriptions.length === 1 ? (
            <p className='text-sm text-slate-800'>{changeDescriptions[0]}</p>
          ) : (
            <ul className='text-sm text-slate-800 list-disc list-inside space-y-1'>
              {changeDescriptions.map(description => (
                <li key={description}>{description}</li>
              ))}
            </ul>
          )}
        </div>

        {preview.capped && (
          <div className='p-3 bg-amber-50 border border-amber-200 rounded-lg'>
            <p className='text-sm text-amber-800'>
              El filtro selecciona {preview.matched} assignments, pero una operación está limitada a{' '}
              {preview.max_bulk_size}. Solo se modificarán los {preview.affected} más recientes.
            </p>
          </div>
        )}

        {aiCount > 0 && (
          <div className='p-3 bg-red-50 border border-red-300 rounded-lg'>
            <div className='flex items-start gap-2'>
              <ExclamationTriangleIcon className='w-5 h-5 text-red-600 flex-shrink-0 mt-0.5' />
              <div>
                <p className='text-sm font-semibold text-red-800'>
                  {aiCount} de estos assignments pasarán a ser procesables por el AI Agent
                </p>
                <p className='text-sm text-red-700 mt-1'>
                  El agente los cogerá en los próximos minutos y enviará comunicaciones reales (WhatsApp, SMS o email)
                  a {aiCount} deudor{aiCount === 1 ? '' : 'es'}. Esto no se puede deshacer.
                </p>
                <label className='flex items-center gap-2 mt-3 text-sm text-red-800 cursor-pointer'>
                  <input
                    type='checkbox'
                    className='form-checkbox rounded-lg border-red-400 text-red-600 focus:ring-red-500'
                    checked={aiAcknowledged}
                    onChange={e => setAiAcknowledged(e.target.checked)}
                  />
                  Entiendo que se enviarán comunicaciones reales a los deudores
                </label>
              </div>
            </div>
          </div>
        )}

        <div className='flex justify-end gap-2 pt-2'>
          <button
            onClick={onCancel}
            disabled={applying}
            className='btn border-slate-200 hover:border-slate-300 text-slate-600 rounded-lg'
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={blocked || applying}
            className='btn bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {applying ? 'Aplicando…' : `Modificar ${preview.affected}`}
          </button>
        </div>
      </div>
    </ModalSimple>
  );
};

export default BulkConfirmModal;
